import { useState } from 'react'
import {
  CheckCircle,
  Close,
  ImageOutlined,
  PictureAsPdf,
  Upload,
} from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import { Accept, ErrorCode, useDropzone } from 'react-dropzone'
import { PopupDialog } from '@/components/popup-dialog'

export type DropzoneFile = {
  key: string
  name: string
  previewUrl?: string
  isPdf?: boolean
}

const errorMessages: Record<ErrorCode, string> = {
  'file-too-large': 'Arquivo muito grande',
  'file-invalid-type': 'Tipo de arquivo inválido',
  'too-many-files': 'Máximo de arquivos atingido',
  'file-too-small': 'Arquivo muito pequeno',
}

const defaultAccept: Accept = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
}

export function DocumentDropzone({
  label,
  description,
  optional,
  required,
  files,
  uploading,
  maxFiles = 5,
  accept,
  approved = false,
  onDrop,
  onRemove,
}: {
  label: string
  description?: string
  optional?: boolean
  required?: boolean
  files: DropzoneFile[]
  uploading?: boolean
  maxFiles?: number
  accept?: Accept
  /** Todos os arquivos já aprovados: só leitura, nada para reenviar. */
  approved?: boolean
  onDrop: (files: File[]) => void
  onRemove: (key: string) => void | Promise<unknown>
}) {
  const [dropErrors, setDropErrors] = useState<string[]>([])
  const [pendingKey, setPendingKey] = useState<string | null>(null)
  const [removing, setRemoving] = useState(false)
  const reached = files.length >= maxFiles
  const narrow = useMediaQuery<Theme>((theme) => theme.breakpoints.down('sm'))

  const { getRootProps, getInputProps, isDragActive, isDragReject } =
    useDropzone({
      accept: accept ?? defaultAccept,
      multiple: true,
      noDragEventsBubbling: true,
      disabled: reached || approved,
      maxFiles: Math.max(maxFiles - files.length, 0),
      onDrop: (accepted) => {
        setDropErrors([])
        if (accepted.length > 0) onDrop(accepted)
      },
      onDropRejected: (rejections) => {
        setDropErrors(
          rejections.map(
            (file) => errorMessages[file.errors[0].code as ErrorCode],
          ),
        )
      },
    })

  const prompt = reached
    ? 'Máximo de arquivos atingido'
    : isDragActive
      ? 'Solte para adicionar o arquivo'
      : narrow
        ? 'Toque para enviar'
        : 'Arraste ou toque para enviar'
  const borderColor = isDragReject
    ? 'error.main'
    : isDragActive
      ? 'primary.main'
      : 'divider'

  async function confirmRemove() {
    if (!pendingKey) return
    setRemoving(true)
    try {
      await onRemove(pendingKey)
      setPendingKey(null)
    } finally {
      setRemoving(false)
    }
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr)',
        gap: 1,
        minWidth: 0,
      }}
    >
      <Typography fontWeight={500}>
        {label}
        {optional && (
          <Typography component="span" color="text.secondary">
            {' '}
            (Opcional)
          </Typography>
        )}
        {required && (
          <Typography component="span" color="error">
            {' '}
            *
          </Typography>
        )}
        {approved && (
          <Typography
            component="span"
            variant="body2"
            color="success.main"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              ml: 1,
              verticalAlign: 'middle',
            }}
          >
            <CheckCircle sx={{ fontSize: 18 }} /> Aprovado
          </Typography>
        )}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5 }}>
          {description}
        </Typography>
      )}
      {!approved && (
        <Box
          {...getRootProps()}
          sx={{
            border: '1px dashed',
            borderColor,
            borderRadius: 1.5,
            px: 1.5,
            py: 1.25,
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            minWidth: 0,
            cursor: reached ? 'default' : 'pointer',
            bgcolor: isDragActive ? 'action.focus' : 'transparent',
            '&:hover': {
              borderColor: isDragReject ? 'error.main' : 'primary.main',
              bgcolor: 'action.focus',
            },
          }}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <CircularProgress size={20} />
          ) : (
            <Upload color="primary" fontSize="small" />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500}>
              {prompt}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              PDF, JPG ou PNG
            </Typography>
          </Box>
        </Box>
      )}
      {dropErrors.length > 0 && (
        <Typography variant="caption" color="error">
          {dropErrors.join(', ')}
        </Typography>
      )}
      {files.length > 0 && (
        <Box sx={{ display: 'grid', gap: 0.25 }}>
          {files.map((file) => (
            <Box
              key={file.key}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
              }}
            >
              <FileMark file={file} />
              <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                {file.name}
              </Typography>
              {approved ? (
                <CheckCircle
                  color="success"
                  fontSize="small"
                  aria-label="Arquivo aprovado"
                />
              ) : (
                <IconButton
                  size="small"
                  aria-label="Remover arquivo"
                  onClick={() => setPendingKey(file.key)}
                >
                  <Close fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}
      <PopupDialog
        open={pendingKey !== null}
        callbacks={{
          onConfirm: () => {
            void confirmRemove()
          },
          onClose: () => {
            if (!removing) setPendingKey(null)
          },
        }}
        title="Remover arquivo"
        message="Deseja remover o arquivo?"
        buttonTexts={{ cancel: 'Cancelar', confirm: 'Remover' }}
        destructive
        confirmLoading={removing}
      />
    </Box>
  )
}

function FileMark({ file }: { file: DropzoneFile }) {
  if (file.previewUrl && !file.isPdf) {
    return (
      <Box
        component="img"
        src={file.previewUrl}
        alt=""
        sx={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 1 }}
      />
    )
  }
  if (file.isPdf) return <PictureAsPdf color="error" />
  return <ImageOutlined color="action" />
}
